import {
  AiUsageBreakdownItem,
  AiUsageSourceValue,
} from "services/aiGatewayService";

/**
 * Pure helpers for the usage tables and the allowance card (WP-70). No React, no services —
 * the Spend, Usage, customer-company and customer pages all render the same figures, and these
 * keep them agreeing on how a dollar, a percentage and a source read.
 */

/**
 * Spend is `decimal(18,8)` server-side and a single cheap call can round to nothing at two
 * decimals, so small totals get four. Anything that reads as `$0.00` next to a non-zero call
 * count looks like a bug in the report rather than a very cheap month.
 */
export const formatUsd = (value: number): string => {
  if (value === 0) return "$0.00";
  if (Math.abs(value) < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/** "38%" or "38.5%" — one decimal at most, none when whole. */
export const formatPercent = (value: number): string => {
  const rounded = Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}%`;
};

/** The translation key naming an attribution source; the unattributed key for `null`. */
export const sourceLabelKey = (kind: AiUsageSourceValue | null): string => {
  switch (kind) {
    case AiUsageSourceValue.Chat:
      return "app.aiGateway.usage.sources.chat";
    case AiUsageSourceValue.Workflow:
      return "app.aiGateway.usage.sources.workflow";
    case AiUsageSourceValue.AutonomousAgent:
      return "app.aiGateway.usage.sources.autonomousAgent";
    case AiUsageSourceValue.Portal:
      return "app.aiGateway.usage.sources.portal";
    case AiUsageSourceValue.Admin:
      return "app.aiGateway.usage.sources.admin";
    default:
      return "app.aiGateway.usage.unattributed";
  }
};

export type UsageBreakdownKind =
  | "customerCompany"
  | "customer"
  | "source"
  | "workflow"
  | "autonomousAgent";

/**
 * A row's name. Sources translate by kind (the server sends the enum name, which has no locale);
 * everything else shows the resolved name, falls back to the raw id, and reads "Not attributed"
 * for the rows written before attribution existed or by the admin app's own AI calls.
 */
export const usageRowLabel = (
  item: AiUsageBreakdownItem,
  kind: UsageBreakdownKind,
  t: (key: string) => string,
): string => {
  // An absent key is null or "" depending on the serializer. It is the unattributed group
  // whatever label the server put on it (English), and for a source it is never 0 (= Chat).
  const raw = item.key?.trim();
  if (!raw) return t("app.aiGateway.usage.unattributed");
  if (kind === "source") {
    const parsed = Number(raw);
    return t(
      sourceLabelKey(
        Number.isNaN(parsed) ? null : (parsed as AiUsageSourceValue),
      ),
    );
  }
  return item.label?.trim() || raw;
};

export type AllowanceTone = "none" | "ok" | "warning" | "exhausted";

/** Warn from 80% used — the same level the default alert thresholds use. */
const WARNING_PERCENT = 80;

export const allowanceTone = (standing: {
  enforced: boolean;
  exhausted: boolean;
  percentUsed: number;
}): AllowanceTone => {
  if (!standing.enforced) return "none";
  if (standing.exhausted) return "exhausted";
  return standing.percentUsed >= WARNING_PERCENT ? "warning" : "ok";
};

/**
 * "2 Sep – 9 Sep 2026" for an explicit window — the tile sub-label when the Dashboard's shared
 * date range drives the report instead of a "Last N days" look-back. Rendered in UTC because the
 * range picker sends whole UTC days (`T00:00:00Z` → `T23:59:59.999Z`); in a local zone east of UTC
 * the end would otherwise print as the next day.
 */
export const formatWindow = (fromIso: string, toIso: string): string => {
  const from = new Date(fromIso);
  const to = new Date(toIso);
  const day = (d: Date, withYear: boolean) =>
    d.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      timeZone: "UTC",
      ...(withYear ? { year: "numeric" } : {}),
    });
  return `${day(from, from.getFullYear() !== to.getFullYear())} – ${day(to, true)}`;
};
